import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { apiClient } from "../../lib/apiClient";
import type { UserPlan } from "../../lib/planCapabilities";

// Every /admin/* route in App.tsx was previously a bare <Route path=... />
// with no guard at all — same shape as a public marketing page. The
// backend API (backend/cmd/api/main.go's `admin` group) IS correctly
// gated (RequireAuth + RequireAdmin, confirmed live via a clean
// cookie-free curl returning 401 "missing bearer token"), so no real
// data was ever exposed. But the frontend rendered the full admin page
// shell (AdminLayout's header/nav/title) for ANY visitor — logged out or
// not, admin or not — before the data fetch failed. A non-admin visitor
// got a confusing "Could not load X" error state instead of a redirect,
// and the admin section's existence/structure was fully visible in the
// rendered DOM regardless of auth. Found live 2026-08-18.
//
// Reuses the exact plan check Dashboard.tsx already uses for isAdmin
// (userPlan === "admin", sourced from GET /profile) rather than
// inventing a second way to ask "is this user an admin" — a route guard
// and a UI-affordance check disagreeing about who's an admin would be
// its own bug class.
//
// Deliberately redirects to "/" rather than a 404: this is a client-side
// SPA route, so the route names are already visible in the compiled JS
// bundle to anyone who looks — a 404 wouldn't hide that they exist, it
// would just be a less standard UX than landing back on the app.
interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [plan, setPlan] = useState<UserPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Not logged in — nothing to fetch, and GET /profile would 401
      // anyway. Resolve the loading state so the redirect below fires.
      setPlanLoading(false);
      return;
    }
    apiClient
      .get<{ plan: UserPlan }>("/profile")
      .then(({ data }) => setPlan(data.plan))
      // Any failure (401 from an expired session, network error, ...)
      // must fail CLOSED — leave plan null so the redirect below fires,
      // never assume admin on an error.
      .catch(() => setPlan(null))
      .finally(() => setPlanLoading(false));
  }, [user, authLoading]);

  // Render nothing while either check is in flight — the alternative
  // (rendering children optimistically) is exactly the flash-of-admin-
  // content this component exists to prevent.
  if (authLoading || (user && planLoading)) return null;

  if (!user || plan !== "admin") return <Navigate to="/" replace />;

  return <>{children}</>;
};

export default AdminRoute;
